"use client";

import Image from "next/image";
import Grid from "../Grid";
import Flex from "../Flex";
import { cn } from "@/shared/libs/commonUtils";
import ImageGallerySlider from "./Image-Gallery-Slider";
import Typography from "../Typography";
import { Slideshow } from "@phosphor-icons/react";
import ImageGalleryModal from "./Image-Gallery-Modal";

import emptyImage from "@/public/svg/empty-image.svg";
import { useDeviceType } from "@/shared/context/DeviceContext";

type Variants = {
  layout?: "grid" | "carousel" | "masonry";
  size?: "small" | "medium" | "large";
  hoverEffect?: "zoom" | "fade" | "none";
  imageRatio?: "square" | "landscape" | "portrait";
  spacing?: "tight" | "normal" | "wide";
};

interface ImageGalleryProps {
  images: string[];
  variants?: Variants;
}

export default function ImageGallery({
  images,
  variants = {},
}: ImageGalleryProps) {
  const deviceType = useDeviceType();
  const BIG_PICTURE = images[0];
  const SMALL_PICTURES = [...images, ...new Array(7).fill(emptyImage)].slice(
    1,
    7,
  );

  return (
    <Grid className="w-full grid-cols-12 grid-rows-1 h-72 max-md:h-auto rounded-xs place-conent-center gap-1">
      {/* - * - * - Big Picture - * - * - */}
      <Grid.GridCol className="relative bg-cover col-span-5 max-lg:col-span-8 max-md:col-span-full row-span-full rounded-xs cursor-pointer group overflow-hidden">
        {/* - * - Mobile gallery - * - */}
        <ImageGallerySlider
          deviceType={deviceType}
          className="md:hidden h-full"
          images={images}
          options={{
            loop: true,
            align: "center",
          }}
        />

        {/* - * - Desktop & Laptop gallery - * - */}
        <ImageGalleryModal
          className="max-md:hidden"
          content={
            <ImageGallerySlider deviceType={deviceType} images={images} />
          }
        >
          <StyledImage img={BIG_PICTURE} />
          <Flex className="w-auto gap-x-2 absolute left-0 bottom-0 -translate-y-4 translate-x-6 bg-background rounded-full items-center justify-center py-2 px-2 shadow-5xl group transition duration-200">
            <Typography
              variant="body3"
              className="hidden group-hover:block transition duration-200"
            >
              مشاهده تصاویر بیشتر
            </Typography>

            <Slideshow size={20} className="text-muted-foreground" />
          </Flex>
        </ImageGalleryModal>
      </Grid.GridCol>

      {/* - * - * - Small Pictures - * - * - */}
      <Grid.GridCol className="col-span-7 max-lg:col-span-4 max-md:hidden">
        <Grid className="h-full grid-cols-3 max-lg:grid-cols-2 grid-rows-2 place-conent-center gap-1">
          {SMALL_PICTURES.map((img, index) => (
            <Grid.GridCol
              className={cn(
                "relative col-span-1 max-lg:col-span-full row-span-1 rounded-xs cursor-pointer group overflow-hidden",
                index > 1 ? "max-lg:hidden" : "",
              )}
              key={`image-${index}`}
            >
              <ImageGalleryModal
                content={
                  <ImageGallerySlider
                    deviceType={deviceType}
                    images={images}
                    options={{ startIndex: index + 1 }}
                  />
                }
              >
                <StyledImage img={img} />
              </ImageGalleryModal>
            </Grid.GridCol>
          ))}
        </Grid>
      </Grid.GridCol>
    </Grid>
  );
}

const StyledImage = ({
  img,
  className,
}: {
  img: string;
  className?: string;
}) => (
  <Image
    className={cn(
      "w-full h-full bg-muted rounded-xs group-hover:scale-[108%] object-cover object-center transition ease-in-out duration-200",
      className,
    )}
    src={img}
    fill
    alt="hotel-image"
    loading="lazy"
    placeholder="blur"
    blurDataURL="https://placehold.co/600x400.png?text=404"
  />
);
